import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Gamepad2, Monitor, Smartphone, DollarSign, Shield } from '../lib/icons';
import ContentSlot from './ContentSlot';

const FAQ = () => {
  const [openCategory, setOpenCategory] = useState<string | null>('ps5');
  const [openQuestion, setOpenQuestion] = useState<string | null>(null);

  const faqData = {
    ps5: {
      title: 'PS5 FAQ',
      icon: Gamepad2,
      color: 'bg-[#00BFFF]',
      questions: [
        {
          id: 'ps5-hdmi',
          question: 'Do you fix HDMI port issues?',
          answer: "Yes! It's our most common PS5 repair.\nSymptoms: No signal, bent pins, loose port.\nTurnaround: Same-day if dropped off early (3–5 hours typical)."
        },
        {
          id: 'ps5-overheating',
          question: 'Do you fix overheating problems?',
          answer: "Absolutely—and it's critical.\nCause: Liquid metal dries or pools from vertical orientation.\nSymptoms: Shutdowns during games (starts at 2 hrs, worsens over time).\nFix: Full cleaning & liquid metal reapplication.\nBonus: Add our $80 cleaning plan for 2 annual deep cleanings."
        },
        {
          id: 'ps5-power',
          question: 'Do you fix no power issues?',
          answer: "Yes. If it's the power supply, we have replacements on hand. If it's motherboard-related, we need a few days to diagnose properly."
        },
        {
          id: 'ps5-disc',
          question: 'Do you fix disc drives or if something is jammed inside?',
          answer: "Yes, we fix unreadable disc drives and remove foreign objects."
        },
        {
          id: 'ps5-fan-connector',
          question: 'Do you fix ripped fan connectors from DIY cleaning?',
          answer: "Yes. We can replace damaged fan connectors or flex cables caused by accidental yanks."
        },
        {
          id: 'ps5-buy',
          question: 'Do you buy broken PS5 consoles?',
          answer: "Yes—but we must check for bans, roach contamination, and general condition before making an offer."
        },
        {
          id: 'ps5-roach',
          question: 'Is there a fee for roach contamination?',
          answer: "Yes. We quarantine for 24–48 hours and charge a $75 roach fee."
        },
        {
          id: 'ps5-warranty',
          question: 'What if something else breaks after the HDMI repair?',
          answer: "Our warranty only covers the HDMI repair. If a different issue arises, we'll inspect and quote it fairly."
        },
        {
          id: 'ps5-pads',
          question: 'Do you fix ripped HDMI pads?',
          answer: "Yes. First jumper is free, additional ones are $10 each."
        },
        {
          id: 'ps5-parts',
          question: 'How do you keep track of screws and parts?',
          answer: "We use magnetic mats and dedicated bins for every device."
        },
        {
          id: 'ps5-quality',
          question: 'What quality control steps do you follow?',
          answer: "We test power, image signal, take diode readings, and photo-document every step before and after repair."
        },
        {
          id: 'ps5-relationship',
          question: 'What if your wife finds out about your girlfriend and throws your PS5 across the room?',
          answer: "We'll fix your console—but not your relationship."
        }
      ]
    },
    xbox: {
      title: 'Xbox FAQ',
      icon: Monitor,
      color: 'bg-[#39FF14]',
      questions: [
        {
          id: 'xbox-hdmi',
          question: 'Do you fix HDMI port issues?',
          answer: "Yes. It's one of the most common Xbox repairs. We carry replacements for Series S and Series X."
        },
        {
          id: 'xbox-overheating',
          question: 'Do you fix overheating problems?',
          answer: "Yes—and it's one of the most common reasons for Xbox shutoffs. We offer internal cleaning and fan maintenance."
        },
        {
          id: 'xbox-power',
          question: 'Do you fix no power issues?',
          answer: "Yes. If it's the power supply, it's usually a fast fix. If it's motherboard-related, we need a few days to diagnose properly."
        },
        {
          id: 'xbox-connectors',
          question: 'Do you fix broken connectors?',
          answer: "Yes. We stock USB-A, USB-C, and fan connectors for Series S and X for faster turnaround."
        },
        {
          id: 'xbox-buy',
          question: 'Do you buy broken Xbox consoles?',
          answer: "Yes, but we inspect condition, functionality, and infestation before making an offer."
        },
        {
          id: 'xbox-roach',
          question: 'What if my Xbox has roaches?',
          answer: "We quarantine for 24–48 hours and charge a $75 cleaning fee."
        },
        {
          id: 'xbox-warranty',
          question: 'What if something else fails later?',
          answer: "Only the original repair is covered. We'll inspect any new issues and quote fairly."
        },
        {
          id: 'xbox-hdmi-process',
          question: 'How do you handle HDMI repairs specifically?',
          answer: "We power on, test diode readings, photo document, verify image, reassemble, and test again."
        }
      ]
    },
    switch: {
      title: 'Nintendo Switch FAQ',
      icon: Smartphone,
      color: 'bg-[#FF1744]',
      questions: [
        {
          id: 'switch-docking',
          question: 'Do you fix HDMI or docking issues?',
          answer: "Yes. Docking issues are often caused by the P13 chip or filters. Bring your dock and charger so we can rule them out."
        },
        {
          id: 'switch-overheating',
          question: 'Do you fix overheating issues?',
          answer: "Yes. We offer an $80 cleaning plan that includes two full cleanings annually."
        },
        {
          id: 'switch-power',
          question: 'Do you fix no power issues?',
          answer: "Yes. We use diode mode testing and USB-C current measurements to determine if M92, BQ, or P13 is the issue. Then we proceed with repair."
        },
        {
          id: 'switch-parts',
          question: 'What parts do you keep in stock?',
          answer: "We stock screens (LCD & OLED), fans, speakers, rails, buttons, connectors, SD/game card readers, and most motherboard chips for faster turnaround."
        },
        {
          id: 'switch-unrepairable',
          question: 'What if my Switch is unrepairable?',
          answer: "We'll offer to buy it for parts. We also sell fully tested refurbished devices with warranty."
        },
        {
          id: 'switch-screen',
          question: 'Do you fix screen damage?',
          answer: "Yes. We carry LCD and OLED screens and can install tempered or anti-glare glass. We also offer custom skins in red, yellow, green, blue, matte, glossy, and many more."
        },
        {
          id: 'switch-accessories',
          question: 'Do you sell screen protectors or accessories?',
          answer: "Yes. We offer tempered glass, anti-glare protectors, and custom vinyl skins (single color or two-tone)."
        },
        {
          id: 'switch-2',
          question: 'Do you support the Switch 2?',
          answer: "Yes! We currently carry USB-C ports (top & bottom) and will expand parts inventory as the repair community identifies more."
        }
      ]
    },
    buyback: {
      title: 'Buyback FAQ',
      icon: DollarSign,
      color: 'bg-[#FF1744]',
      questions: [
        {
          id: 'buyback-devices',
          question: 'Do you buy iPhones, iPads, MacBooks, or game consoles?',
          answer: "Yes. Working or broken—we buy PS5, Xbox, Nintendo Switch, iPhones, iPads, and MacBooks."
        },
        {
          id: 'buyback-repaired',
          question: 'Do you buy iPhones that have been repaired?',
          answer: "Yes. We'll assess part quality and make a fair offer."
        },
        {
          id: 'buyback-icloud',
          question: 'Do you buy iCloud-locked devices?',
          answer: "Yes—but only for parts, and only if they're not blacklisted."
        },
        {
          id: 'buyback-no-power',
          question: 'Do you buy devices that won\'t turn on?',
          answer: "Yes. We'll assist you with remote iCloud sign-out and then wipe the device."
        },
        {
          id: 'buyback-sealed',
          question: 'Do you buy new sealed iPhones?',
          answer: "Yes. We must break the seal and verify it's not fake or blacklisted using a validation service."
        },
        {
          id: 'buyback-battery',
          question: 'Does battery health affect the value?',
          answer: "Yes. Batteries under 85% health reduce device value."
        },
        {
          id: 'buyback-improve',
          question: 'What improves the buyback offer?',
          answer: "Bringing original accessories like Joy-Cons, dock, and charger increases the amount we offer."
        },
        {
          id: 'buyback-payment-device',
          question: 'Can I use a device as payment?',
          answer: "Yes! We accept phones, tablets, or consoles as full or partial payment depending on condition."
        },
        {
          id: 'buyback-payment-methods',
          question: 'What forms of payment do you accept?',
          answer: "Cash, debit, credit, Venmo, Apple Pay, and Zelle. Devices may also be used as payment."
        }
      ]
    },
    warranty: {
      title: 'Warranty FAQ',
      icon: Shield,
      color: 'bg-[#00BFFF]',
      questions: [
        {
          id: 'warranty-length',
          question: 'How long is your warranty?',
          answer: "All standard repairs come with a 90-day warranty. Battery replacements for Switch, laptops, and controllers come with a 12-month warranty."
        },
        {
          id: 'warranty-void',
          question: 'What voids the warranty?',
          answer: "Signs of water damage, pest infestation, or user-caused physical damage void the warranty."
        },
        {
          id: 'warranty-repeat',
          question: 'What if I damage my HDMI port again?',
          answer: "If it breaks within 75 days, we offer a discounted repeat HDMI repair—we get it, things happen."
        },
        {
          id: 'warranty-diagnostics',
          question: 'Are diagnostics included in the warranty?',
          answer: "Post-repair diagnostics for related issues are covered. Unrelated issues may incur a fee."
        },
        {
          id: 'warranty-unrelated',
          question: 'What if something else breaks unrelated to the original repair?',
          answer: "Those aren't covered, but we'll give you a fair quote."
        }
      ]
    }
  };

  const toggleCategory = (category: string) => {
    setOpenCategory(openCategory === category ? null : category);
    setOpenQuestion(null);
  };

  const toggleQuestion = (questionId: string) => {
    setOpenQuestion(openQuestion === questionId ? null : questionId);
  };

  return (
    <section id="faq" className="py-20 bg-[#0D0D0D]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-[#E0E0E0] mb-4">
            <ContentSlot slotId="faq-heading" fallback="Frequently Asked Questions" contentType="heading" pageSection="faq" label="Section Heading" />
          </h2>
          <ContentSlot
            slotId="faq-subtitle"
            fallback="Get answers to common questions about our repair services, warranties, and device buyback program"
            contentType="paragraph"
            as="p"
            pageSection="faq"
            label="Section Subtitle"
            className="text-xl text-[#B0B0B0] max-w-3xl mx-auto"
          />
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center mb-8 gap-2">
            {Object.entries(faqData).map(([key, category]) => (
              <button
                key={key}
                onClick={() => toggleCategory(key)}
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-semibold transition-all duration-200 ${
                  openCategory === key
                    ? `${category.color} text-white shadow-lg`
                    : 'bg-gray-900 text-[#E0E0E0] hover:bg-gray-800 border border-gray-700'
                }`}
              >
                <category.icon className="h-5 w-5" />
                <span>{category.title}</span>
              </button>
            ))}
          </div>

          {/* FAQ Content */}
          {openCategory && (
            <div className="bg-gray-900 rounded-2xl shadow-lg overflow-hidden border border-gray-800">
              <div className={`${faqData[openCategory].color} text-white p-6`}>
                <div className="flex items-center space-x-3">
                  {React.createElement(faqData[openCategory].icon, { className: 'h-8 w-8' })}
                  <h3 className="text-2xl font-bold">{faqData[openCategory].title}</h3>
                </div>
              </div>

              <div className="divide-y divide-gray-800">
                {faqData[openCategory].questions.map((faq) => (
                  <div key={faq.id} className="p-6">
                    <button
                      onClick={() => toggleQuestion(faq.id)}
                      className="w-full flex items-center justify-between text-left hover:bg-gray-800 p-4 rounded-lg transition-colors duration-200"
                    >
                      <h4 className="text-lg font-semibold text-[#E0E0E0] pr-4">
                        {faq.question}
                      </h4>
                      {openQuestion === faq.id ? (
                        <ChevronUp className="h-5 w-5 text-[#B0B0B0] flex-shrink-0" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-[#B0B0B0] flex-shrink-0" />
                      )}
                    </button>

                    {openQuestion === faq.id && (
                      <div className="mt-4 pl-4">
                        <div className="text-[#B0B0B0] leading-relaxed">
                          {faq.answer.split('\n').map((line, index) => (
                            <p key={index} className={index > 0 ? 'mt-2' : ''}>
                              {line}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Call to Action */}
          <div className="mt-12 text-center">
           <div className="bg-gradient-to-r from-[#00BFFF] to-[#FF1744] text-[#0D0D0D] rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-4">
                <ContentSlot slotId="faq-cta-heading" fallback="Still Have Questions?" contentType="subheading" pageSection="faq" label="CTA Heading" />
              </h3>
              <ContentSlot
                slotId="faq-cta-subtitle"
                fallback="Upgrade to TMR Hall Effect Joysticks anti drift joysticks"
                contentType="paragraph"
                as="p"
                pageSection="faq"
                label="CTA Subtitle"
                className="text-[#0D0D0D]/80 mb-6 text-lg"
              />
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:+17602147244"
                  className="bg-[#39FF14] text-[#0D0D0D] px-8 py-3 rounded-lg font-semibold hover:bg-white transition-colors duration-200 shadow-lg"
                >
                  Call San Diego: (760) 214-7244
                </a>
                <a
                  href="tel:+19512168534"
                  className="bg-[#39FF14] text-[#0D0D0D] px-8 py-3 rounded-lg font-semibold hover:bg-white transition-colors duration-200 shadow-lg"
                >
                  Call Temecula: (951) 216-8534
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export default FAQ;